import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { InAppCommunicationQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'

import {
  isValidFeature,
  isValidModule,
  isValidReason,
} from 'modules/in_app_comms/lib/enumValidation'
import {
  buildMarketplaceLink,
  extractPathname,
  getComponentName,
  processLink,
} from 'modules/in_app_comms/lib/marketplaceLinks'
import AskOneButton from 'modules/in_app_comms/components/AskOneButton'
import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

import UpsellButton from './_UpsellButton'

type ButtonType = NonNullable<
  NonNullable<
    InAppCommunicationQuery['inAppComms']['communicationsConnection']['nodes'][number]['content']
  >['banner']
>['buttons'][number]

interface ButtonProps {
  button: ButtonType
  variant: string
  size: string
  componentType: string
  componentName: string
  onClick?: () => void
  extraEventProps?: Record<string, string>
  onUpsellSuccess?: () => void
  onUpsellError?: () => void
  upsellRequestComponentType?: string
}

const CommsButton = ({
  button,
  onClick,
  variant,
  size,
  componentType,
  componentName,
  extraEventProps,
  onUpsellSuccess,
  onUpsellError,
  upsellRequestComponentType,
}: ButtonProps) => {
  const { create } = useCreateUpsellRequest()
  const navigate = useNavigate()
  const { email, company } = useCurrentSession()

  const [storedUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>('upsell_origin', 5)

  const triggerEvent = (action: string) => {
    track(trackingEvents.inAppComms.clickInAppCommsComponent, {
      componentType,
      componentName,
      buttonType: button.type,
      buttonAction: action,
      ...extraEventProps,
    })
  }

  if (!button) return null
  switch (button.type) {
    case 'CommsLinkButtonRecord':
      return (
        <Box style={{ backgroundColor: 'white', borderRadius: '10px' }}>
          <F0Button
            label={button.text}
            onClick={() => {
              let processedLink = processLink(button.link ?? undefined, email, company?.id)
              triggerEvent(`Open link: ${button.link}`)
              if (button.isInternalLink) {
                processedLink = buildMarketplaceLink(
                  processedLink,
                  componentType,
                  componentName,
                  storedUpsellOrigin
                )
                const pathname = extractPathname(processedLink)
                if (button.openInNewTab) {
                  window.open(pathname, '_blank')
                } else {
                  navigate(pathname)
                }
              } else {
                if (button.openInNewTab) {
                  window.open(processedLink, '_blank')
                } else {
                  window.location.href = processedLink
                }
              }
              onClick?.()
            }}
            variant={variant}
            size={size}
          />
        </Box>
      )

    case 'CommsCloseButtonRecord':
      return (
        <F0Button
          label={button.text}
          onClick={() => {
            triggerEvent('Close component')
            onClick?.()
          }}
          variant={variant}
          size={size}
        />
      )

    case 'CommsOpenOneRecord':
      return (
        <AskOneButton
          label={button.text}
          prompt={button.prompt}
          size={size}
          onClick={() => {
            triggerEvent('Open One chat')
            onClick?.()
          }}
        />
      )

    case 'CommsUpsellButtonRecord':
      return (
        <Box style={{ backgroundColor: 'white', borderRadius: '10px' }}>
          <UpsellButton
            size={size}
            label={button.text}
            onClick={async () => {
              const componentNameForUpsell = getComponentName(componentType)
              const requestSourceCategory =
                storedUpsellOrigin?.request_source_category || 'In-app Communication'
              const requestSourceComponent =
                storedUpsellOrigin?.request_source_component || componentNameForUpsell

              const upsellProps = {
                moduleRequested: isValidModule(button.moduleRequested)
                  ? button.moduleRequested
                  : undefined,
                requestSource:
                  button.requestSource || storedUpsellOrigin?.request_source || undefined,
                requestSourceCategory,
                upsellRequestType: 'Outside Marketplace',
                requestSourceComponent,
                requestReason: isValidReason(button.requestReason)
                  ? button.requestReason
                  : undefined,
                featureRequested: isValidFeature(button.featureRequested)
                  ? button.featureRequested
                  : undefined,
                moreInfo: button.moreInfoUrl ?? undefined,
                upsellRequestComponentType: upsellRequestComponentType || 'in_app_comms_button',
              }

              const result = await create(upsellProps)
              const handled = handleResult(result, () => undefined)

              if (handled.success) {
                onUpsellSuccess?.()
              } else {
                onUpsellError?.()
              }

              track(trackingEvents.inAppComms.clickInAppCommsComponent, {
                componentType,
                componentName,
                buttonType: button.type,
                buttonAction: 'Upsell request',
                ...upsellProps,
              })
              onClick?.()
            }}
          />
        </Box>
      )

    default:
      return (
        <Box style={{ backgroundColor: 'white', borderRadius: '10px' }}>
          <F0Button
            label={button.text}
            onClick={() => {
              triggerEvent('Default on click')
              onClick?.()
            }}
            variant={variant}
            size={size}
          />
        </Box>
      )
  }
}

export default CommsButton
